import { motion } from 'framer-motion';
import { Check, Clock, Zap, Users, Award, TrendingUp, HeadphonesIcon } from 'lucide-react';
import { useScrollAnimation, useCountUp } from '../hooks/useScrollAnimation';
import { stats } from '../data';
import { ParticleBackground } from '../components/ParticleBackground';

const features = [
  {
    icon: Clock,
    title: 'Responsif & Cepat',
    description: 'Respon dalam 24 jam untuk setiap kebutuhan Anda',
  },
  {
    icon: Zap,
    title: 'Solusi Efektif',
    description: 'Strategi terbukti dengan hasil nyata dan terukur',
  },
  {
    icon: Users,
    title: 'Tim Ahli Berpengalaman',
    description: 'Profesional dengan pengalaman industri yang relevan',
  },
  {
    icon: Award,
    title: 'Kualitas Terjamin',
    description: 'Standar kualitas tinggi dalam setiap deliverable',
  },
  {
    icon: TrendingUp,
    title: 'Hasil Berkelanjutan',
    description: 'Fokus pada pertumbuhan jangka panjang bisnis Anda',
  },
  {
    icon: HeadphonesIcon,
    title: 'Dukungan Penuh',
    description: 'Pendampingan sebelum, selama, dan setelah project',
  },
];

function StatCard({ stat, index, isInView }: { stat: typeof stats[0]; index: number; isInView: boolean }) {
  const numericValue = parseInt(stat.value);
  const count = useCountUp(numericValue, 2000, isInView);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
      whileHover={{ y: -8, scale: 1.03 }}
      className="relative bg-[#0a1628] rounded-3xl p-8 text-center shadow-lg hover:shadow-2xl hover:shadow-blue-900/20 transition-all duration-500 group overflow-hidden border border-blue-900/30 pointer-events-auto"
    >
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-600/0 to-primary-600/0 group-hover:from-primary-600/5 group-hover:to-accent-500/5 transition-all duration-500" />

      <div className="relative">
        <div className="text-5xl md:text-6xl font-bold mb-2">
          <span className="text-white">{count}</span>
          <span className="text-primary-300">{stat.suffix}</span>
        </div>
        <p className="text-primary-200 font-medium">{stat.label}</p>
      </div>
    </motion.div>
  );
}

export function WhyChooseUs() {
  const { ref, isInView } = useScrollAnimation({ threshold: 0.2 });

  return (
    <section
      id="why-us"
      ref={ref}
      className="relative py-24 lg:py-32 overflow-hidden"
    >
      {/* White Background */}
      <div className="absolute inset-0 bg-white z-0" />
      
      {/* Interactive Particle Background - fills entire section */}
      <div className="absolute inset-0 z-[5]">
        <ParticleBackground />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto section-padding pointer-events-none">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto mb-16 pointer-events-auto"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#0a1628] rounded-full mb-6 border border-blue-900/30 pointer-events-auto"
          >
            <span className="w-2 h-2 bg-white rounded-full" />
            <span className="text-sm font-medium text-blue-300">Mengapa Memilih Kami</span>
          </motion.div>

          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-[#0a1628] mb-6">
            Kelebihan Bekerja dengan
            <span className="text-blue-300"> Kami</span>
          </h2>

          <p className="text-lg text-gray-600">
            Kami berkomitmen memberikan layanan terbaik dengan standar profesional tertinggi
          </p>
        </motion.div>

        {/* Stats Section */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-6 mb-20 pointer-events-auto">
          {stats.map((stat, index) => (
            <StatCard key={stat.id} stat={stat} index={index} isInView={isInView} />
          ))}
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 pointer-events-auto">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                whileHover={{ y: -8 }}
                className="flex gap-5 p-6 rounded-2xl bg-[#0a1628] border border-blue-900/30 shadow-lg hover:shadow-2xl hover:border-blue-700/50 transition-all duration-300 group pointer-events-auto"
              >
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-300 rounded-xl flex items-center justify-center group-hover:bg-blue-200 transition-all duration-300">
                    <Icon className="w-6 h-6 text-[#0a1628]" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white mb-1 group-hover:text-primary-300 transition-colors duration-300">
                    {feature.title}
                  </h3>
                  <p className="text-primary-200 text-sm">{feature.description}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="mt-16 p-8 md:p-12 rounded-3xl bg-[#0a1628] border border-blue-900/30 shadow-xl"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h3 className="text-2xl font-bold text-white mb-2">
                Siap Memulai Kerja Sama?
              </h3>
              <p className="text-primary-200">
                Diskusikan kebutuhan bisnis Anda dengan tim ahli kami
              </p>
            </div>
            <motion.a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 px-8 py-4 bg-blue-300 text-[#0a1628] font-semibold rounded-full hover:bg-blue-200 transition-colors duration-300 shadow-xl shadow-blue-300/30 whitespace-nowrap pointer-events-auto cursor-pointer"
            >
              <Check className="w-5 h-5" />
              Mulai Sekarang
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}